const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
const PREFIX = "wm-reveal-outbox-v1:"

// Use cryptographic randomness even on browsers without crypto.randomUUID.
const eventId = () => {
	const bytes = new Uint8Array(16)
	window.crypto.getRandomValues(bytes)
	bytes[6] = (bytes[6] & 15) | 64
	bytes[8] = (bytes[8] & 63) | 128
	const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("")
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

const retryDelay = (error, attempts, now) => {
	const backoff = Math.min(60000, 1000 * 2 ** Math.min(attempts - 1, 6))
	const headers = error.response && error.response.headers
	const retryAfter = headers && (headers["retry-after"] || headers["Retry-After"])
	if (!retryAfter) return backoff
	const seconds = Number(retryAfter)
	const delay = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(retryAfter) - now
	return Number.isFinite(delay) ? Math.max(backoff, delay) : backoff
}

// Persist one entry per event; neither another tab nor another account owns it.
// Keep only the active credential in memory. Old sessions resume from storage
// only when their own credential is explicitly restored.
export const createRevealAnswerOutbox = ({ send, storage, now = () => Date.now(), makeEventId = eventId }) => {
	let active = null
	// What this page has seen happen to its own events, for deliveryState(). Kept
	// apart from the sessions because a session's queue is dropped on sign-out,
	// and a GOT IT pressed later in the page should still be able to say so.
	const delivered = new Set()
	const refused = new Map()

	const removeKey = key => {
		try {
			storage.removeItem(key)
		} catch (e) {
			/* A duplicate replay is server-idempotent. */
		}
	}
	const remove = (session, event) => {
		if (session.key) removeKey(session.key + event.event_id)
	}
	// Both directions: a click stamped a week ahead (a clock set far wrong) would otherwise
	// never expire, and if the server refused it, it would sit in storage for good.
	const expired = event => Math.abs(now() - Date.parse(event.occurred_at)) >= MAX_AGE_MS
	const persist = (session, event) => {
		if (!session.key) return
		if (expired(event)) {
			remove(session, event)
			return
		}
		try {
			storage.setItem(session.key + event.event_id, JSON.stringify(event))
		} catch (e) {
			// Storage denial/quota failure still permits in-memory retries.
		}
	}
	// Structural checks only. A click stamped ahead of the device clock as it
	// reads now (the clock was set back after the click) is still a real click:
	// it sends age_ms 0 and the server places it at receipt, so it is kept.
	const normalize = item => {
		if (
			!item ||
			!Number.isInteger(item.question_id) ||
			item.question_id <= 0 ||
			!/^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(item.event_id) ||
			typeof item.occurred_at !== "string" ||
			!Number.isFinite(Date.parse(item.occurred_at))
		)
			return null
		return {
			question_id: item.question_id,
			event_id: item.event_id,
			occurred_at: item.occurred_at,
			attempts: Number.isInteger(item.attempts) && item.attempts >= 0 ? item.attempts : 0,
			next_attempt_at: Number.isFinite(item.next_attempt_at) ? item.next_attempt_at : now(),
			rejected: Number.isInteger(item.rejected) ? item.rejected : null,
		}
	}
	// Cleanup does not require authentication and never delivers another session's
	// entries. Inactive, revoked and 401-paused sessions need retention too.
	const readAndClean = session => {
		const entries = []
		try {
			storage
				.keys()
				.filter(key => key.startsWith(PREFIX))
				.forEach(key => {
					let event = null
					try {
						event = normalize(JSON.parse(storage.getItem(key)))
					} catch (e) {
						/* Invalid entry. */
					}
					if (!event || expired(event)) {
						removeKey(key)
						return
					}
					if (session && session.key && key.startsWith(session.key)) entries.push(event)
				})
		} catch (e) {
			/* Storage may be unavailable; keep memory entries. */
		}
		return entries
	}
	const prune = session => {
		session.events = session.events.filter(event => {
			if (!expired(event)) return true
			remove(session, event)
			return false
		})
	}
	const merge = session => {
		const stored = readAndClean(session)
		if (!session) return
		prune(session)
		const known = new Map(session.events.map(event => [event.event_id, event]))
		stored.forEach(event => {
			const existing = known.get(event.event_id)
			if (existing) {
				// Another tab's throttle deadline must survive a reconnect here too.
				existing.next_attempt_at = Math.max(existing.next_attempt_at, event.next_attempt_at)
				existing.attempts = Math.max(existing.attempts, event.attempts)
				existing.rejected = existing.rejected || event.rejected
			} else {
				session.events.push(event)
				known.set(event.event_id, event)
			}
		})
		session.events.sort((a, b) => Date.parse(a.occurred_at) - Date.parse(b.occurred_at))
	}
	// Wire body: event_id and occurred_at are byte-identical on every attempt.
	// age_ms is how long ago the click happened on this device's own clock,
	// measured afresh at each send and never persisted. Device clocks can be
	// minutes out, which makes occurred_at unusable on its own; both readings
	// in age_ms come from the same clock, so the error cancels and the server
	// places the click at receipt minus age. A clock set backwards since the
	// click would give a negative age, so it is floored at zero.
	const payload = event => ({
		event_id: event.event_id,
		occurred_at: event.occurred_at,
		age_ms: Math.max(0, Math.round(now() - Date.parse(event.occurred_at))),
	})
	const flush = async session => {
		if (session !== active) return
		prune(session)
		if (session.sending || session.timer || session.unauthorized) return
		const event = session.events.find(item => !item.rejected)
		if (!event) return
		const wait = event.next_attempt_at - now()
		if (wait > 0) {
			session.timer = setTimeout(() => {
				session.timer = null
				flush(session)
			}, Math.min(wait, 60000))
			return
		}
		session.sending = true
		try {
			await send(event.question_id, payload(event), session.token)
			delivered.add(event.event_id)
			remove(session, event)
			session.events = session.events.filter(item => item.event_id !== event.event_id)
		} catch (error) {
			const status = error && error.response && error.response.status
			if (status === 401) session.unauthorized = true
			else if (!status || status === 408 || status === 429 || status >= 500) {
				event.attempts += 1
				event.next_attempt_at = now() + retryDelay(error || {}, event.attempts, now())
			} else {
				event.rejected = status
				refused.set(event.event_id, status)
			}
			persist(session, event)
		} finally {
			session.sending = false
			flush(session)
		}
	}
	const resume = () => {
		merge(active)
		if (active) flush(active)
	}
	const setSession = token => {
		if (active && active.timer) {
			clearTimeout(active.timer)
			active.timer = null
		}
		if (!token) {
			active = null
			readAndClean(null)
			return
		}
		if (!active || active.token !== token) {
			const match = /^(\d+)\|/.exec(token)
			active = {
				token,
				key: match ? PREFIX + match[1] + ":" : null,
				events: [],
				sending: false,
				timer: null,
				unauthorized: false,
			}
		}
		active.unauthorized = false
		resume()
	}
	// Returns the event synchronously, so the GOT IT that follows can carry the
	// same event_id and click time (the server de-duplicates by event_id). The
	// event is created even with no active session: it is then neither stored
	// nor sent from here, and travels only with the GOT IT.
	const record = questionId => {
		const event = {
			question_id: questionId,
			event_id: makeEventId(),
			occurred_at: new Date(now()).toISOString(),
			attempts: 0,
			next_attempt_at: now(),
		}
		if (active && Number.isInteger(questionId) && questionId > 0) {
			active.events.push(event)
			persist(active, event)
			flush(active)
		}
		return { event_id: event.event_id, occurred_at: event.occurred_at }
	}
	// What this page knows of one event's delivery, sent with the GOT IT so the
	// server can tell a click still on its way from one that will never come.
	// delivered: accepted by the server during this page's life.
	// pending: queued or retrying in the active session.
	// refused_status: the status the server rejected it with, or null.
	// paused: delivery is stopped (a 401, or no active session).
	const deliveryState = id => {
		const queued = active ? active.events.find(event => event.event_id === id) : undefined
		const rejected = (queued && queued.rejected) || refused.get(id) || null
		return {
			delivered: delivered.has(id),
			pending: Boolean(queued && !queued.rejected),
			refused_status: rejected,
			paused: !active || active.unauthorized,
		}
	}
	return { setSession, record, resume, deliveryState }
}
